import { Row, notification, Button, Icon } from 'antd';
import { Panel } from 'components/ui/index';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Permission } from 'components/page/index';
import PERMISSIONS from 'config/app-permission.config';
import Enums from 'utils/EnumsManager';
import { toUtc } from 'utils/dateTimeUtils';
import { Actions, Fields } from '../components/index';
import { reset, setSuccess, trySaveNewTask, tryUpdateTask, setRouteInfo } from '../flow/actions';
import { resetModuleReadonlyMode, setModuleReadonlyMode } from '../../DetailsSubpanels/flow/actions';

const { PhantomId, ThemeTypesInArray, DetailModules } = Enums;
const { TaskOpen } = DetailModules;
const getRelateToId = value => value.split('__')[1];
const getRelatedToType = value => value.split('__')[0];

const mapStoreToRequest = ({
  assigneeId,
  comments,
  dueTime,
  priorityCode,
  statusCode,
  subject,
  relatedTo,
}, objectId, objectType) => ({
  assign_to_user_id: assigneeId,
  subject,
  status_code: statusCode,
  priority_code: priorityCode,
  due_date: toUtc(dueTime),
  comments,
  taskable_id: Number(getRelateToId(relatedTo)),
  taskable_type: getRelatedToType(relatedTo),
});


const propTypes = {
  intl: intlShape.isRequired,
  objectId: PropTypes.string.isRequired,
  theme: PropTypes.oneOf(ThemeTypesInArray).isRequired,
};


class TaskDetails extends Component {
  componentDidUpdate() {
    const {
      history,
      objectId,
      objectType,
      synced,
      routeInfo,
      setRouteInfo,
    } = this.props;
    if (synced === 'save' || (synced === 'cancel' && routeInfo !== 'dashboard')) {
      history.push(`/${objectType}/${objectId}`);
    }
    if (synced === 'cancel' && routeInfo === 'dashboard') {
      history.push('/dashboard');
    }
    if (synced === 'saveAndNew') {
      this.props.reset();
      setRouteInfo(objectType);
      history.push(`/${objectType}/${objectId}/tasks/${PhantomId}`);
    }
  }

  componentWillUnmount() {
    const { resetModuleReadonlyMode } = this.props;
    resetModuleReadonlyMode(TaskOpen);
    this.props.reset();
  }

  handleCancel = () => this.props.setSuccess('cancel')

  handleSave = (action) => {
    const {
      objectId,
      objectType,
      taskDetails,
      taskId,
      trySaveNewTask,
      tryUpdateTask,
    } = this.props;

    const isValid = this.checkValidation(taskDetails);
    if (!isValid) return;

    const targetData = mapStoreToRequest(taskDetails, objectId, objectType);
    if (taskId === PhantomId) {
      trySaveNewTask(taskId, targetData, action);
    } else {
      tryUpdateTask(taskId, targetData, action);
    }
  }

  handleSaveAndNew = () => this.handleSave('saveAndNew')

  showNotification = message => notification.error({ duration: 3, message })

  checkValidation = (data) => {
    const { intl } = this.props;
    const { formatMessage } = intl;
    const i18n = 'global.errors';
    const {
      assigneeId,
      dueTime,
      priorityCode,
      statusCode,
      subject,
    } = data;

    if (assigneeId === '') {
      this.showNotification(formatMessage({ id: `${i18n}.assigneeRequired` }));
      return false;
    }
    if (dueTime === '') {
      this.showNotification(formatMessage({ id: `${i18n}.dateRequired` }));
      return false;
    }
    if (priorityCode === '') {
      this.showNotification(formatMessage({ id: `${i18n}.priorityRequired` }));
      return false;
    }
    if (statusCode === '') {
      this.showNotification(formatMessage({ id: `${i18n}.statusRequired` }));
      return false;
    }
    if (_.isEmpty(subject)) {
      this.showNotification(formatMessage({ id: `${i18n}.subjectRequired` }));
      return false;
    }
    return true;
  }

  render() {
    const {
      intl,
      defaultStateId,
      objectId,
      objectType,
      taskId,
      theme,
      disableActions,
      isHistoryTask,
      readOnlyMode,
      setModuleReadonlyMode,
      convertDefaultValue,
    } = this.props;

    const { formatMessage } = intl;
    const i18nPrefix = 'page.taskDetails';
    let titleKey = '';
    if (isHistoryTask) {
      titleKey = 'viewTitle';
    } else if (taskId === PhantomId) {
      titleKey = 'newTitle';
    } else {
      titleKey = 'editTitle';
    }
    const editBtn = !isHistoryTask && readOnlyMode ? (
      <Permission permission={PERMISSIONS[`${TaskOpen.toUpperCase()}_UPDATE`]}>
        <Button
          size="small"
          onClick={() => setModuleReadonlyMode(TaskOpen)}
        >
          <Icon size="small" type="edit" />
          {formatMessage({ id: 'global.ui.button.edit' })}
        </Button>
      </Permission>
    ) : null;
    
    return (
      <Panel
        panelTitle={formatMessage({ id: `${i18nPrefix}.${titleKey}` })}
        panelClasses={`${theme}-theme-panel`}
        actionsRight={editBtn}
      >
        <Row className="mt-md mb-md ml-lg mr-lg">
          <Fields
            defaultStateId={defaultStateId}
            objectId={objectId}
            objectType={objectType}
            taskId={taskId}
            isHistoryTask={isHistoryTask}
            readOnlyMode={readOnlyMode}
            convertDefaultValue={convertDefaultValue}
          />
        </Row>
        {!disableActions && (
          <Row className="mt-md mb-md ml-lg mr-lg">
            <Actions
              objectId={objectId}
              objectType={objectType}
              onCancel={this.handleCancel}
              onDelete={this.handleDelete}
              onSave={() => this.handleSave('save')}
              onSaveAndNew={this.handleSaveAndNew}
              isHistoryTask={isHistoryTask}
              readOnlyMode={readOnlyMode}
            />
          </Row>
        )}
      </Panel>
    );
  }
}


TaskDetails.propTypes = propTypes;
const mapStateToProps = ({ global, taskDetails, clientDetails }) => ({
  language: global.language,
  taskDetails,
  synced: taskDetails.synced,
  resetTask: taskDetails.resetTask,
  routeInfo: taskDetails.routeInfo,
  readOnlyMode: clientDetails.subpanels.moduleReadonlyMode[`${TaskOpen}ReadonlyMode`],
});
const mapDispatchToProps = {
  reset,
  setSuccess,
  trySaveNewTask,
  tryUpdateTask,
  setRouteInfo,
  resetModuleReadonlyMode,
  setModuleReadonlyMode,
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(injectIntl(TaskDetails)));



// WEBPACK FOOTER //
// ./src/components/page/TaskDetails/container/index.jsx